import {
    createContext 
} from 'react';
import theme from '../../figmaTheme';
import {
    Choice,
    DiagnosticAnswers
} from '../../components/diagnostic/config/types';
import {
    MATIntakeAnswers, 
    MATPreIntakeAnswers
} from '../../components/diagnostic/config/mat-types';
import SunnysideMedLogo from '../../../public/icons/sunnyside/sunnysideMed';

type Section = {
    key: string,
    title?: string,
    headerText?: string,
    headerLogo?: JSX.Element,
    length: number,
    colors: {
        background: string,
        main: string,
        titleText?: string,
        titleBackground?: string,
        titleBorder?: string,
        headerText: string,
        choiceText: string,
        choice: string,
        ctaBackground: string,
        ctaButton: string,
        disabledCTAButton?: string,
        ctaButtonText: string,
        ctaText: string,
        buttonArrow: string,
        circleTakeover: string,
        trophy?: string
    },
    icon: JSX.Element | null,
    inProgressBar: boolean,
    /* Challenge section only */
    challengeName?: string,
    challengeGraphic?: JSX.Element | null,
    challengeDescription?: string,
    challengeOptions?: Choice[],
    challengeIdentifier?: string,
    challengeSkipUrl?: string,
    showHeader?: boolean,
    hasTrophy?: boolean
}

type Answers = DiagnosticAnswers | MATIntakeAnswers | MATPreIntakeAnswers;

export type FormContextValue = {
    formId: string,
    type: string,
    answers: Answers,
    setAnswers: (answers: any) => void,
    screenDirection: 'left' | 'right',
    setScreenDirection: (screenDirection: 'left' | 'right') => void,
    sections: Section[],
    goBack: (jump?: number) => void,
    goNext: (jump?: number) => void,
    fireTrackingEvents: (formContext: FormContextValue, trackingName: string, cookies: any) => void,
    screenIndex: number,
    currentScreenName: string,
    startTime: number | null,
    completeDiagnostic: () => void
}

export type DiagnosticFormContextValue = FormContextValue & {
    answers: DiagnosticAnswers,
    setEndGoal?: (answers: any) => void,
    setCustomTargets?: (answers: any) => void,
    setPronouns?: (answers: any) => void,
    startAboutYouSection?: () => void,
    skippySkipsIntro?: () => void,
    skippySkipsGoals?: () => void,
    skippySkipsName?: () => void,
    skippySkipsEmail?: (answers: any) => void,
    skippySkipsPhone?: () => void
}

export type ChallengeFormContextValue = FormContextValue & {
    answers: DiagnosticAnswers,
    completeChallengeFlow?: () => void
}

export type MATPreIntakeFormContextValue = FormContextValue & {
    answers: MATPreIntakeAnswers
}

export type MATFormContextValue = FormContextValue & {
    answers: MATIntakeAnswers,
    setPrescription?: (formContext: MATFormContextValue, answers: any) => void,
    hasPrescriptionsId?: string,
    prescriptionsId?: string,
    setAllergies?: (formContext: MATFormContextValue, answers: any) => void,
    hasAllergiesId?: string,
    allergiesId?: string
}

const defaultFormContextValue: FormContextValue = {
    formId: '',
    type: '',
    answers: {},
    setAnswers: (answers: any) => void(answers),
    screenDirection: 'left',
    setScreenDirection: (screenDirection: 'left' | 'right') => void(screenDirection),
    sections: [],
    goBack: (jump?: number) => void(jump),
    goNext: (jump?: number) => void(jump),
    fireTrackingEvents: () => void(0),
    screenIndex: 0,
    currentScreenName: '',
    startTime: null,
    completeDiagnostic: () => void(0),
};

const defaultDiagnosticFormContextValue = defaultFormContextValue as DiagnosticFormContextValue;
const defaultChallengeFormContextValue = defaultFormContextValue as ChallengeFormContextValue;
const defaultMATPreIntakeFormContextValue = defaultFormContextValue as MATPreIntakeFormContextValue;
const defaultMATFormContextValue = defaultFormContextValue as MATFormContextValue;

export const defaultSignupSections: Section[] = [
    {
        key: 'goals',
        headerText: 'Your Goals',
        length: 7,
        colors: {
            background: theme.palette.primary['100'],
            main: theme.palette.primary.main,
            headerText: theme.palette.primary.dark,
            choiceText: theme.palette.primary.dark,
            choice: theme.palette.primary.light,
            ctaBackground: theme.palette.white,
            ctaButton: theme.palette.primary.main,
            ctaButtonText: theme.palette.white,
            ctaText: theme.palette.neutral.main,
            buttonArrow: theme.palette.white,
            circleTakeover: theme.palette.primary['200']
        },
        icon: (
            <svg
                style={{
                    width: '100%',
                    height: '100%' 
                }}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5.9998 3.075C4.38437 3.075 3.0748 4.38457 3.0748 6C3.0748 7.61544 4.38437 8.925 5.9998 8.925C7.61524 8.925 8.9248 7.61544 8.9248 6C8.9248 4.38457 7.61524 3.075 5.9998 3.075ZM2.1748 6C2.1748 3.88751 3.88732 2.175 5.9998 2.175C8.11229 2.175 9.8248 3.88751 9.8248 6C9.8248 8.11249 8.11229 9.825 5.9998 9.825C3.88732 9.825 2.1748 8.11249 2.1748 6Z"
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5.9998 0.825003C3.14173 0.825003 0.824805 3.14193 0.824805 6C0.824805 8.85808 3.14173 11.175 5.9998 11.175C8.85788 11.175 11.1748 8.85808 11.1748 6C11.1748 3.14193 8.85788 0.825003 5.9998 0.825003ZM-0.0751953 6C-0.0751953 2.64487 2.64467 -0.0749969 5.9998 -0.0749969C9.35493 -0.0749969 12.0748 2.64487 12.0748 6C12.0748 9.35513 9.35493 12.075 5.9998 12.075C2.64467 12.075 -0.0751953 9.35513 -0.0751953 6Z"
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5.9998 5.325C5.62701 5.325 5.3248 5.62721 5.3248 6C5.3248 6.37279 5.62701 6.675 5.9998 6.675C6.3726 6.675 6.6748 6.37279 6.6748 6C6.6748 5.62721 6.3726 5.325 5.9998 5.325ZM4.4248 6C4.4248 5.13015 5.12996 4.425 5.9998 4.425C6.86965 4.425 7.5748 5.13015 7.5748 6C7.5748 6.86985 6.86965 7.575 5.9998 7.575C5.12996 7.575 4.4248 6.86985 4.4248 6Z"
                />
            </svg>            
        ),
        inProgressBar: true
    },
    {
        key: 'habits',
        headerText: 'Your Habits',
        length: 20,
        colors: {
            background: theme.palette.secondary['100'],
            main: theme.palette.secondary.main,
            headerText: theme.palette.secondary.dark,
            choiceText: theme.palette.secondary.dark,
            choice: theme.palette.secondary.light,
            ctaBackground: theme.palette.white,
            ctaButton: theme.palette.secondary.main,
            ctaButtonText: theme.palette.white,
            ctaText: theme.palette.neutral.main,
            buttonArrow: theme.palette.white,
            circleTakeover: theme.palette.secondary['200']
        },
        icon: (
            <svg
                style={{
                    width: '100%',
                    height: '100%' 
                }}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M2.07268 0.150024C1.82415 0.150024 1.62268 0.351496 1.62268 0.600024V3.54548C1.62268 5.80212 3.35095 7.6727 5.54993 7.89965V10.95H3.54543C3.29691 10.95 3.09543 11.1515 3.09543 11.4C3.09543 11.6486 3.29691 11.85 3.54543 11.85H8.45452C8.70305 11.85 8.90452 11.6486 8.90452 11.4C8.90452 11.1515 8.70305 10.95 8.45452 10.95H6.44993V7.89965C8.64893 7.67273 10.3772 5.80214 10.3772 3.54548V0.600024C10.3772 0.351496 10.1758 0.150024 9.92723 0.150024H2.07268ZM2.52268 3.09548V1.05002H9.47723V3.09548H2.52268ZM2.55192 3.99548C2.77456 5.69668 4.24088 7.02275 5.99995 7.02275C7.75902 7.02275 9.22534 5.69668 9.44798 3.99548H2.55192Z"
                />
            </svg> 
        ),
        inProgressBar: true
    },
    {
        key: 'aboutYou',
        headerText: 'About You',
        length: 6,
        colors: {
            background: theme.palette.warning['100'],
            main: theme.palette.warning.main,
            headerText: theme.palette.warning.dark,
            choiceText: theme.palette.warning.dark,
            choice: theme.palette.warning.light,
            ctaBackground: theme.palette.white,
            ctaButton: theme.palette.warning.main,
            ctaButtonText: theme.palette.white,
            ctaText: theme.palette.neutral.main,
            buttonArrow: theme.palette.white,
            circleTakeover: theme.palette.warning['200']
        },
        icon: (
            <svg
                style={{
                    width: '100%',
                    height: '100%' 
                }}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3.58584 5.01815C3.58584 3.68514 4.66646 2.60452 5.99948 2.60452C7.33249 2.60452 8.41311 3.68514 8.41311 5.01815V5.99997C8.41311 7.33299 7.33249 8.41361 5.99948 8.41361C4.66646 8.41361 3.58584 7.33299 3.58584 5.99997V5.01815ZM5.99948 3.50452C5.16352 3.50452 4.48584 4.18219 4.48584 5.01815V5.99997C4.48584 6.83593 5.16352 7.51361 5.99948 7.51361C6.83543 7.51361 7.51311 6.83593 7.51311 5.99997V5.01815C7.51311 4.18219 6.83543 3.50452 5.99948 3.50452Z"
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5.9999 0.150024C2.76904 0.150024 0.149902 2.76916 0.149902 6.00002C0.149902 9.23089 2.76904 11.85 5.9999 11.85C9.23077 11.85 11.8499 9.23089 11.8499 6.00002C11.8499 2.76916 9.23077 0.150024 5.9999 0.150024ZM1.0499 6.00002C1.0499 3.26622 3.26609 1.05002 5.9999 1.05002C8.73371 1.05002 10.9499 3.26622 10.9499 6.00002C10.9499 6.94905 10.6828 7.8357 10.2197 8.58891L9.14617 8.05212C8.92388 7.94098 8.65358 8.03108 8.54243 8.25337C8.43129 8.47566 8.52139 8.74596 8.74368 8.85711L9.67087 9.3207C8.76512 10.3214 7.45592 10.95 5.9999 10.95C4.54388 10.95 3.23469 10.3214 2.32894 9.3207L3.25613 8.85711C3.47842 8.74596 3.56852 8.47566 3.45738 8.25337C3.34623 8.03108 3.07593 7.94098 2.85364 8.05212L1.78006 8.58891C1.31697 7.8357 1.0499 6.94905 1.0499 6.00002Z"
                />
            </svg> 
        ),
        inProgressBar: true
    },
    {
        key: 'plan',
        headerText: 'Your Plan',
        length: 30,
        colors: {
            background: theme.palette.tertiary['100'],
            main: theme.palette.tertiary.main,
            headerText: theme.palette.tertiary.dark,
            choiceText: theme.palette.tertiary.dark,
            choice: theme.palette.tertiary.light,
            ctaBackground: theme.palette.white,
            ctaButton: theme.palette.tertiary.main,
            ctaButtonText: theme.palette.white,
            ctaText: theme.palette.neutral.main,
            buttonArrow: theme.palette.black,
            circleTakeover: theme.palette.tertiary['200']
        },
        icon: (
            <svg
                style={{
                    width: '100%',
                    height: '100%' 
                }}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3.0549 0.150002C3.30343 0.150064 3.50485 0.351586 3.50479 0.600114L3.50465 1.13203H8.49567L8.4958 0.599889C8.49587 0.351361 8.69739 0.149939 8.94592 0.150002C9.19444 0.150064 9.39587 0.351586 9.3958 0.600114L9.39567 1.13203H10.0504C11.0445 1.13203 11.8504 1.93792 11.8504 2.93203V4.07754V4.97754V10.05C11.8504 11.0441 11.0445 11.85 10.0504 11.85H1.95039C0.956278 11.85 0.150391 11.0441 0.150391 10.05V4.97754V4.07754V2.93203C0.150391 1.93792 0.956278 1.13203 1.95039 1.13203H2.60465L2.60479 0.599889C2.60485 0.351361 2.80637 0.149939 3.0549 0.150002ZM8.49545 2.03203L8.49531 2.56353C8.49525 2.81205 8.69667 3.01358 8.9452 3.01364C9.19373 3.0137 9.39525 2.81228 9.39531 2.56375L9.39545 2.03203H10.0504C10.5474 2.03203 10.9504 2.43498 10.9504 2.93203V4.07754H1.05039V2.93203C1.05039 2.43498 1.45333 2.03203 1.95039 2.03203H2.60443L2.6043 2.56353C2.60423 2.81205 2.80566 3.01358 3.05418 3.01364C3.30271 3.0137 3.50423 2.81228 3.5043 2.56375L3.50443 2.03203H8.49545ZM10.9504 4.97754V10.05C10.9504 10.5471 10.5474 10.95 10.0504 10.95H1.95039C1.45333 10.95 1.05039 10.5471 1.05039 10.05V4.97754H10.9504Z"
                />
            </svg>
        ),
        inProgressBar: true        
    },
    {
        key: 'checkout',
        headerText: 'Membership',
        length: 1,
        colors: {
            background: theme.palette.success['100'],
            main: theme.palette.success.main,
            headerText: theme.palette.success.dark,
            choiceText: theme.palette.success.dark,
            choice: theme.palette.success.light,
            ctaBackground: theme.palette.primary.light,
            ctaButton: theme.palette.primary.main,
            ctaButtonText: theme.palette.white,
            ctaText: theme.palette.neutral.main,
            buttonArrow: theme.palette.white,
            circleTakeover: theme.palette.success['200']
        },
        icon: (
            <svg
                style={{
                    width: '100%',
                    height: '100%' 
                }}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5.32771 1.14468C5.6028 0.587188 6.39776 0.587192 6.67285 1.14468L7.96773 3.76879L10.8634 4.18953C11.4786 4.27891 11.7242 5.03488 11.2791 5.46879L9.18379 7.51132L9.67827 10.3953C9.78333 11.008 9.14018 11.4752 8.58997 11.1858L6.00028 9.82392L3.41059 11.1858C2.86038 11.4752 2.21723 11.008 2.32229 10.3953L2.81678 7.51132L0.72144 5.46879C0.276314 5.03488 0.521957 4.27891 1.13712 4.18953L4.03284 3.76879L5.32771 1.14468ZM6.00028 1.81554L4.80502 4.23777C4.69578 4.45915 4.48459 4.6126 4.24029 4.6481L1.5673 5.03647L3.50147 6.9219C3.67821 7.09419 3.75887 7.34242 3.71716 7.5857L3.26071 10.2478L5.65119 8.99064C5.86973 8.87572 6.13083 8.87572 6.34937 8.99064L8.73985 10.2478L8.28341 7.5857C8.24169 7.34242 8.32235 7.09419 8.4991 6.9219L10.4333 5.03647L7.76027 4.6481C7.51597 4.6126 7.30478 4.45916 7.19554 4.23777L6.00028 1.81554Z"
                />
            </svg>
        ),
        inProgressBar: true
    }
];

export const defaultMATSections: Section[] = [
    {
        key: 'Habits',
        title: 'Habits',
        headerLogo: <SunnysideMedLogo />,
        length: 12,
        colors: {
            background: theme.palette.primary['900'],
            main: theme.palette.primary['900'],
            headerText: theme.palette.white,
            choiceText: theme.palette.primary.dark,
            choice: theme.palette.primary.light,
            ctaBackground: theme.palette.primary['900'],
            ctaButton: theme.palette.white,
            ctaButtonText: theme.palette.primary['900'],
            ctaText: theme.palette.neutral['900'],
            buttonArrow: theme.palette.white,
            circleTakeover: theme.palette.primary['200']
        },
        icon: (
            <svg
                style={{
                    width: '100%',
                    height: '100%' 
                }}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M2.07268 0.150024C1.82415 0.150024 1.62268 0.351496 1.62268 0.600024V3.54548C1.62268 5.80212 3.35095 7.6727 5.54993 7.89965V10.95H3.54543C3.29691 10.95 3.09543 11.1515 3.09543 11.4C3.09543 11.6486 3.29691 11.85 3.54543 11.85H8.45452C8.70305 11.85 8.90452 11.6486 8.90452 11.4C8.90452 11.1515 8.70305 10.95 8.45452 10.95H6.44993V7.89965C8.64893 7.67273 10.3772 5.80214 10.3772 3.54548V0.600024C10.3772 0.351496 10.1758 0.150024 9.92723 0.150024H2.07268ZM2.52268 3.09548V1.05002H9.47723V3.09548H2.52268ZM2.55192 3.99548C2.77456 5.69668 4.24088 7.02275 5.99995 7.02275C7.75902 7.02275 9.22534 5.69668 9.44798 3.99548H2.55192Z"
                />
            </svg>            
        ),
        inProgressBar: true
    },
    {
        key: 'Medical History',
        title: 'Medical History',
        headerLogo: <SunnysideMedLogo />,
        length: 7,
        colors: {
            background: theme.palette.primary['900'],
            main: theme.palette.primary['900'],
            headerText: theme.palette.white,
            choiceText: theme.palette.primary.dark,
            choice: theme.palette.primary.light,
            ctaBackground: theme.palette.primary['900'],
            ctaButton: theme.palette.white,
            ctaButtonText: theme.palette.primary['900'],
            ctaText: theme.palette.neutral['900'],
            buttonArrow: theme.palette.white,
            circleTakeover: theme.palette.primary['200']
        },
        icon: (
            <svg
                style={{
                    width: '100%',
                    height: '100%' 
                }}
                width="35"
                height="35"
                viewBox="0 0 35 35"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.69231 2.69231C9.69231 1.20539 10.8977 0 12.3846 0H22.6154C24.1023 0 25.3077 1.20539 25.3077 2.69231V9.69231H32.3077C33.7946 9.69231 35 10.8977 35 12.3846V22.6154C35 24.1023 33.7946 25.3077 32.3077 25.3077H25.3077V32.3077C25.3077 33.7946 24.1023 35 22.6154 35H12.3846C10.8977 35 9.69231 33.7946 9.69231 32.3077V25.3077H2.69231C1.20539 25.3077 0 24.1023 0 22.6154V12.3846C0 10.8977 1.20539 9.69231 2.69231 9.69231H9.69231V2.69231ZM22.6154 2.69231L12.3846 2.69231V10.3654C12.3846 11.4806 11.4806 12.3846 10.3654 12.3846L2.69231 12.3846V22.6154H10.3654C11.4806 22.6154 12.3846 23.5194 12.3846 24.6346V32.3077H22.6154V24.6346C22.6154 23.5194 23.5194 22.6154 24.6346 22.6154H32.3077V12.3846H24.6346C23.5194 12.3846 22.6154 11.4806 22.6154 10.3654V2.69231Z"
                />
            </svg>
        ),
        inProgressBar: true
    },
    {
        key: 'identity',
        title: 'Identity Verification',
        headerLogo: <SunnysideMedLogo />,
        length: 3,
        colors: {
            background: theme.palette.primary['900'],
            main: theme.palette.primary['900'],
            headerText: theme.palette.white,
            choiceText: theme.palette.primary.dark,
            choice: theme.palette.primary.light,
            ctaBackground: theme.palette.primary['900'],
            ctaButton: theme.palette.white,
            ctaButtonText: theme.palette.primary['900'],
            ctaText: theme.palette.neutral['900'],
            buttonArrow: theme.palette.white,
            circleTakeover: theme.palette.primary['200']
        },
        icon: (
            <svg
                style={{
                    width: '100%',
                    height: '100%' 
                }}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3.58584 5.01815C3.58584 3.68514 4.66646 2.60452 5.99948 2.60452C7.33249 2.60452 8.41311 3.68514 8.41311 5.01815V5.99997C8.41311 7.33299 7.33249 8.41361 5.99948 8.41361C4.66646 8.41361 3.58584 7.33299 3.58584 5.99997V5.01815ZM5.99948 3.50452C5.16352 3.50452 4.48584 4.18219 4.48584 5.01815V5.99997C4.48584 6.83593 5.16352 7.51361 5.99948 7.51361C6.83543 7.51361 7.51311 6.83593 7.51311 5.99997V5.01815C7.51311 4.18219 6.83543 3.50452 5.99948 3.50452Z"
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5.9999 0.150024C2.76904 0.150024 0.149902 2.76916 0.149902 6.00002C0.149902 9.23089 2.76904 11.85 5.9999 11.85C9.23077 11.85 11.8499 9.23089 11.8499 6.00002C11.8499 2.76916 9.23077 0.150024 5.9999 0.150024ZM1.0499 6.00002C1.0499 3.26622 3.26609 1.05002 5.9999 1.05002C8.73371 1.05002 10.9499 3.26622 10.9499 6.00002C10.9499 6.94905 10.6828 7.8357 10.2197 8.58891L9.14617 8.05212C8.92388 7.94098 8.65358 8.03108 8.54243 8.25337C8.43129 8.47566 8.52139 8.74596 8.74368 8.85711L9.67087 9.3207C8.76512 10.3214 7.45592 10.95 5.9999 10.95C4.54388 10.95 3.23469 10.3214 2.32894 9.3207L3.25613 8.85711C3.47842 8.74596 3.56852 8.47566 3.45738 8.25337C3.34623 8.03108 3.07593 7.94098 2.85364 8.05212L1.78006 8.58891C1.31697 7.8357 1.0499 6.94905 1.0499 6.00002Z"
                />
            </svg>            
        ),
        inProgressBar: true
    },
    {
        key: 'complete',
        length: 1,
        colors: {
            background: theme.palette.primary['900'],
            main: theme.palette.primary['900'],
            headerText: theme.palette.white,
            choiceText: theme.palette.primary.dark,
            choice: theme.palette.primary.light,
            ctaBackground: theme.palette.primary['900'],
            ctaButton: theme.palette.white,
            ctaButtonText: theme.palette.primary['900'],
            ctaText: theme.palette.neutral['900'],
            buttonArrow: theme.palette.white,
            circleTakeover: theme.palette.primary['200']
        },
        icon: (
            <svg
                style={{
                    width: '100%',
                    height: '100%' 
                }}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M11.6631 2.74017C12.1235 3.18281 12.1104 3.88823 11.6337 4.31577L5.42193 9.8872C4.96485 10.2972 4.24284 10.3052 3.77537 9.90543L0.38714 7.00829C-0.100389 6.59143 -0.131678 5.8865 0.317256 5.43379C0.766189 4.98109 1.52534 4.95203 2.01287 5.3689L4.56857 7.55417L9.96631 2.71285C10.443 2.2853 11.2027 2.29754 11.6631 2.74017Z"
                />
            </svg>
        ),
        inProgressBar: true
    }
];

export const defaultMATPreIntakeSections: Section[] = [
    {
        key: 'naltrexone',
        title: 'What is Naltrexone?',
        headerLogo: <SunnysideMedLogo isDark />,
        length: 5,
        colors: {
            background: theme.palette.white,
            main: theme.palette.primary['900'],
            titleText: theme.palette.primary['900'],
            titleBackground: theme.palette.transparent,
            titleBorder: theme.palette.primary['900'],
            headerText: theme.palette.primary['900'],
            choiceText: theme.palette.primary['900'],
            choice: theme.palette.primary.light,
            ctaBackground: theme.palette.white,
            ctaButton: theme.palette.primary['900'],
            ctaButtonText: theme.palette.white,
            ctaText: theme.palette.neutral['900'],
            buttonArrow: theme.palette.white,
            circleTakeover: theme.palette.primary['200']
        },
        icon: null,
        inProgressBar: false
    },
    {
        key: 'sunnysideMed',
        title: 'Introducing Sunnyside Med',
        headerLogo: <SunnysideMedLogo isDark />,
        length: 7,
        colors: {
            background: theme.palette.white,
            main: theme.palette.primary['900'],
            titleText: theme.palette.primary['900'],
            titleBackground: theme.palette.transparent,
            titleBorder: theme.palette.primary['900'],
            headerText: theme.palette.primary['900'],
            choiceText: theme.palette.primary['900'],
            choice: theme.palette.primary.light,
            ctaBackground: theme.palette.white,
            ctaButton: theme.palette.primary['900'],
            ctaButtonText: theme.palette.white,
            ctaText: theme.palette.neutral['900'],
            buttonArrow: theme.palette.white,
            circleTakeover: theme.palette.primary['200']
        },
        icon: null,
        inProgressBar: false
    },
    {
        key: 'qualify',
        title: 'Qualify for Treatment',
        headerLogo: <SunnysideMedLogo />,
        length: 11,
        colors: {
            background: theme.palette.primary['900'],
            main: theme.palette.white,
            titleText: theme.palette.white,
            titleBackground: theme.palette.primary['900'],
            titleBorder: theme.palette.white,
            headerText: theme.palette.white,
            choiceText: theme.palette.primary['900'],
            choice: theme.palette.white,
            ctaBackground: theme.palette.primary['900'],
            ctaButton: theme.palette.white,
            disabledCTAButton: '#93A8B5',
            ctaButtonText: theme.palette.primary['900'],
            ctaText: theme.palette.primary['900'],
            buttonArrow: theme.palette.primary['900'],
            circleTakeover: theme.palette.primary['200']
        },
        icon: null,
        inProgressBar: false
    }
];

export const defaultMedicationSection: Section = {
    key: 'med',
    headerLogo: <SunnysideMedLogo />,
    length: 1,
    colors: {
        background: theme.palette.primary['900'],
        main: theme.palette.primary['900'],
        headerText: theme.palette.white,
        choiceText: theme.palette.primary.dark,
        choice: theme.palette.primary.light,
        ctaBackground: theme.palette.primary['900'],
        ctaButton: theme.palette.white,
        ctaButtonText: theme.palette.primary['900'],
        ctaText: theme.palette.neutral['900'],
        buttonArrow: theme.palette.white,
        circleTakeover: theme.palette.primary['200']
    },
    icon: null,
    inProgressBar: false
};

export const defaultDequeueSection: Section = {
    key: 'dequeue',
    headerLogo: <SunnysideMedLogo isDark />,
    length: 1,
    colors: {
        background: theme.palette.white,
        main: theme.palette.primary['900'],
        headerText: theme.palette.white,
        choiceText: theme.palette.primary.dark,
        choice: theme.palette.primary.light,
        ctaBackground: theme.palette.white,
        ctaButton: theme.palette.primary['900'],
        ctaButtonText: theme.palette.white,
        ctaText: theme.palette.primary['900'],
        buttonArrow: theme.palette.white,
        circleTakeover: theme.palette.primary['200']
    },
    icon: null,
    inProgressBar: false
};

export const FormContext = createContext(defaultFormContextValue);
export const DiagnosticFormContext = createContext(defaultDiagnosticFormContextValue);
export const ChallengeFormContext = createContext(defaultChallengeFormContextValue);
export const MATPreIntakeFormContext = createContext(defaultMATPreIntakeFormContextValue);
export const MATFormContext = createContext(defaultMATFormContextValue);
